/** Official Moonshot K2.6 list prices, CNY per 1M tokens, excluding tax. */
export const KIMI_K26_CNY_PER_MILLION = {
  inputUncached: 6.5,
  cacheRead: 1.1,
  cacheWrite: 6.5,
  output: 27,
} as const;

export interface KimiK26Usage {
  inputTokens: number;
  cacheReadInputTokens: number;
  cacheCreationInputTokens: number;
  outputTokens: number;
}

export interface KimiK26TurnCost {
  usage: KimiK26Usage;
  cny: number;
  title: string;
}

export interface ConversationAiCost {
  totalCny: number;
  turns: number;
}

const USAGE_LINE = /(?:^|\n)usage:\s*(\{[^\n]*\})/;
const USAGE_FIELDS = [
  'input_tokens',
  'cache_read_input_tokens',
  'cache_creation_input_tokens',
  'output_tokens',
] as const;

function tokenCount(value: unknown): number {
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return parsed;
}

export function parseUsageFromThinking(thinking: string | null | undefined): KimiK26Usage | null {
  if (!thinking) return null;
  const match = thinking.match(USAGE_LINE);
  if (!match?.[1]) return null;
  try {
    const parsed: unknown = JSON.parse(match[1]);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    const record = parsed as Record<string, unknown>;
    const hasField = USAGE_FIELDS.some((field) => Number.isFinite(Number(record[field])));
    if (!hasField) return null;
    return {
      inputTokens: tokenCount(record.input_tokens),
      cacheReadInputTokens: tokenCount(record.cache_read_input_tokens),
      cacheCreationInputTokens: tokenCount(record.cache_creation_input_tokens),
      outputTokens: tokenCount(record.output_tokens),
    };
  } catch {
    return null;
  }
}

export function estimateKimiK26Cny(usage: KimiK26Usage): number {
  const million = 1_000_000;
  return (
    (usage.inputTokens * KIMI_K26_CNY_PER_MILLION.inputUncached
      + usage.cacheReadInputTokens * KIMI_K26_CNY_PER_MILLION.cacheRead
      + usage.cacheCreationInputTokens * KIMI_K26_CNY_PER_MILLION.cacheWrite
      + usage.outputTokens * KIMI_K26_CNY_PER_MILLION.output)
    / million
  );
}

export function formatCny(amount: number): string {
  if (amount > 0 && amount < 0.01) return `¥${amount.toFixed(4)}`;
  return `¥${amount.toFixed(2)}`;
}

function formatTokens(count: number): string {
  return Math.round(count).toLocaleString('zh-CN');
}

export function usageTitle(usage: KimiK26Usage): string {
  return [
    `未命中 ${formatTokens(usage.inputTokens)}`,
    `命中 ${formatTokens(usage.cacheReadInputTokens)}`,
    `写入 ${formatTokens(usage.cacheCreationInputTokens)}`,
    `输出 ${formatTokens(usage.outputTokens)}`,
  ].join(' · ');
}

export function outboundTurnCost(message: {
  direction?: string;
  thinking?: string | null;
}): KimiK26TurnCost | null {
  if (message.direction && message.direction !== 'out') return null;
  const usage = parseUsageFromThinking(message.thinking);
  if (!usage) return null;
  return {
    usage,
    cny: estimateKimiK26Cny(usage),
    title: usageTitle(usage),
  };
}

export function summarizeConversationAiCost(
  messages: ReadonlyArray<{ direction?: string; thinking?: string | null }>,
): ConversationAiCost {
  let totalCny = 0;
  let turns = 0;
  for (const message of messages) {
    const turn = outboundTurnCost(message);
    if (!turn) continue;
    totalCny += turn.cny;
    turns += 1;
  }
  return { totalCny, turns };
}
